import { useCallback, useEffect, useState } from 'react';

import { stopRouting } from './stopRouting';

/**
 * Abstracts Next.js router interface to avoid adding Next.js as a direct dependency.
 */
type RouterForNavigationGuard = {
  push: (url: string) => void;
  events: {
    on: (event: 'routeChangeStart', handler: (url: string) => void) => void;
    off: (event: 'routeChangeStart', handler: (url: string) => void) => void;
  };
};

type UseNavigationWithUnsavedChangesOptions = {
  router: RouterForNavigationGuard;
  hasUnsavedChanges: boolean;
  /**
   * Predicate to allow navigation to specific URLs without showing the modal.
   * When not provided, all navigations with unsaved changes will trigger the modal.
   */
  allowNavigation?: (url: string) => boolean;
};

type UseNavigationWithUnsavedChangesReturn = {
  shouldShowMessage: boolean;
  onConfirmNavigation: () => void;
  onCancelNavigation: () => void;
};

export const useNavigationWithUnsavedChanges = ({
  router,
  hasUnsavedChanges,
  allowNavigation,
}: UseNavigationWithUnsavedChangesOptions): UseNavigationWithUnsavedChangesReturn => {
  const [shouldShowMessage, setShouldShowMessage] = useState(false);
  const [nextURL, setNextURL] = useState<string | null>(null);
  const [hasUserConfirmed, setHasUserConfirmed] = useState(false);

  const handleRouteChange = useCallback(
    (url: string) => {
      setNextURL(url);

      if (allowNavigation?.(url)) {
        return;
      }

      if (hasUnsavedChanges && !hasUserConfirmed) {
        setShouldShowMessage(true);
        stopRouting();
      }
    },
    [hasUnsavedChanges, hasUserConfirmed, allowNavigation],
  );

  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events, handleRouteChange]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const onConfirmNavigation = useCallback(() => {
    setHasUserConfirmed(true);
    setShouldShowMessage(false);
    nextURL && router.push(nextURL);
  }, [router, nextURL]);

  const onCancelNavigation = useCallback(() => {
    setShouldShowMessage(false);
  }, []);

  return {
    shouldShowMessage,
    onConfirmNavigation,
    onCancelNavigation,
  };
};
